import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import { attachDeployDetector } from './deployDetector';

const M = '/api/method';

const base = (import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL.trim()) || window.location.origin;

// Create axios instance with base configuration
const api = axios.create({
  baseURL: base,
  timeout: 30000,
});

// Token refresh management
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeToTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(newToken: string) {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
}

// Check if token expires soon (within 5 minutes)
function isTokenExpiringSoon(): boolean {
  const expiresAt = localStorage.getItem('auth_token_expires_at');
  if (!expiresAt) return false;

  const expiresDate = new Date(expiresAt);
  const now = new Date();
  const fiveMinutes = 5 * 60 * 1000;

  return (expiresDate.getTime() - now.getTime()) < fiveMinutes;
}

// Refresh the access token using the refresh token
async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post(
      `${base}${M}/ski_delivery.api._auth_resort_admin.refresh_token`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const data = response.data.message || response.data;
    if (data.jwt) {
      // Update stored tokens
      localStorage.setItem('auth_token', data.jwt);
      localStorage.setItem('auth_token_expires_at', new Date(Date.now() + data.expires_in * 1000).toISOString());
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('refresh_token_expires_at', new Date(Date.now() + data.refresh_token_expires_in * 1000).toISOString());

      // Keep the cached user role/scope in sync with the freshly-issued token.
      // The rotated token always carries the user's CURRENT backend role, so if
      // an admin changes this user's role the UI must follow — otherwise it keeps
      // gating on the stale login-time role (e.g. showing User Management to a
      // user the backend has downgraded, which then returns nothing -> blank).
      if (data.role) {
        try {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            const u = JSON.parse(storedUser);
            if (u.role !== data.role || u.scope_type !== data.scope_type) {
              const updated = { ...u, role: data.role, scope_type: data.scope_type ?? u.scope_type };
              localStorage.setItem('user', JSON.stringify(updated));
              window.dispatchEvent(new CustomEvent('auth-user-updated', { detail: updated }));
            }
          }
        } catch (e) {
          console.error('Failed to sync cached user after token refresh:', e);
        }
      }
      return data.jwt;
    }
    return null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
}

// Add request interceptor to include auth token if available
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      // Check if token is expiring soon and we have a refresh token
      const refreshToken = localStorage.getItem('refresh_token');
      if (isTokenExpiringSoon() && refreshToken && !isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            config.headers['X-Resort-Admin-Auth'] = `Bearer ${newToken}`;
            onTokenRefreshed(newToken);
          } else {
            config.headers['X-Resort-Admin-Auth'] = `Bearer ${token}`;
          }
        } finally {
          isRefreshing = false;
        }
      } else if (isRefreshing) {
        // Another request is refreshing the token, wait for it
        return new Promise(resolve => {
          subscribeToTokenRefresh((newToken: string) => {
            config.headers!['X-Resort-Admin-Auth'] = `Bearer ${newToken}`;
            resolve(config);
          });
        });
      } else {
        config.headers['X-Resort-Admin-Auth'] = `Bearer ${token}`;
      }
    }
    // JSON body for Frappe is fine, but don't override FormData
    if (config.method !== 'get' && config.headers && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Basic error normalization with silent token refresh
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const originalRequest = error.config;
    const status = error?.response?.status;
    const data = error?.response?.data;

    // Helper function to check if error message indicates token expiration
    const isTokenExpiredError = (message: string): boolean => {
      const lowerMsg = message.toLowerCase();
      return lowerMsg.includes('token has expired') ||
             lowerMsg.includes('token expired') ||
             lowerMsg.includes('invalid token');
    };

    // Helper function to handle auth expiration
    const handleAuthExpired = () => {
      try {
        sessionStorage.setItem('auth_expired', '1');
        try {
          const currentPath = window.location.pathname + window.location.search + window.location.hash;
          sessionStorage.setItem('auth_expired_path', currentPath);
        } catch {
          // storage is unavailable in some privacy modes, and this path must not fail because of it
        }
      } catch {
        // storage is unavailable in some privacy modes, and this path must not fail because of it
      }
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_token_expires_at');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('refresh_token_expires_at');
      localStorage.removeItem('user');
      window.dispatchEvent(new CustomEvent('auth-expired'));
    };

    // Check if this is a 401 error and we haven't tried to refresh yet
    if (status === 401 && !originalRequest._retry) {
      const refreshToken = localStorage.getItem('refresh_token');

      // Only attempt refresh if we have a refresh token
      if (refreshToken) {
        originalRequest._retry = true;

        // If already refreshing, wait for it
        if (isRefreshing) {
          return new Promise(resolve => {
            subscribeToTokenRefresh((newToken: string) => {
              originalRequest.headers['X-Resort-Admin-Auth'] = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            });
          });
        }

        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          if (newToken) {
            onTokenRefreshed(newToken);
            originalRequest.headers['X-Resort-Admin-Auth'] = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Refresh failed or no refresh token, logout
      handleAuthExpired();
      return Promise.reject(new Error('Session expired. Please log in again.'));
    }

    if (typeof data?.error_code === 'string') {
      return Promise.reject(error);
    }

    // First, check for Frappe _server_messages (validation errors) - handle before auth check
    // This prevents validation errors from being treated as auth failures
    if (data?._server_messages) {
      try {
        const serverMessages = JSON.parse(data._server_messages);
        if (Array.isArray(serverMessages) && serverMessages.length > 0) {
          // Each message is also JSON-encoded
          const firstMsg = JSON.parse(serverMessages[0]);
          if (firstMsg.message) {
            // Check if this is a token expiration error
            if (isTokenExpiredError(firstMsg.message)) {
              handleAuthExpired();
              return Promise.reject(new Error('Authentication expired. Please log in again.'));
            }
            return Promise.reject(new Error(firstMsg.message));
          }
        }
      } catch (e) {
        // If parsing fails, continue to other handlers
      }
    }

    // Check for exc_type to identify Frappe validation errors
    if (data?.exc_type === 'ValidationError') {
      const msg = data?._error_message || data?.exception || 'Validation error';
      // Check if this is a token expiration error
      if (isTokenExpiredError(msg)) {
        handleAuthExpired();
        return Promise.reject(new Error('Authentication expired. Please log in again.'));
      }
      return Promise.reject(new Error(msg));
    }

    // Treat 403 as authentication failure (401 handled above with refresh)
    if (status === 403) {
      handleAuthExpired();
      return Promise.reject(new Error('Authentication expired. Please log in again.'));
    }

    // Treat 417 as auth failure only if it's a token expiration error, not a missing/wrong header
    if (status === 417) {
      const serverMsg = data?._server_messages;
      let is417TokenExpired = false;
      if (serverMsg) {
        try {
          const msgs = JSON.parse(serverMsg);
          const firstMsg = JSON.parse(msgs[0]);
          is417TokenExpired = isTokenExpiredError(firstMsg.message || '');
        } catch {
          // a server message that is not JSON simply means this is not the expired-token case
        }
      }
      if (is417TokenExpired) {
        handleAuthExpired();
        return Promise.reject(new Error('Authentication expired. Please log in again.'));
      }
      // Non-auth 417 errors (e.g. wrong auth header for endpoint) - just reject without logout
      return Promise.reject(new Error(data?._error_message || 'Request failed'));
    }

    // Handle 422 validation errors with better message extraction
    if (status === 422) {
      // Try multiple possible error message locations
      if (data?._error_message) {
        return Promise.reject(new Error(data._error_message));
      }
      if (data?.exception) {
        return Promise.reject(new Error(data.exception));
      }
      // Pydantic field-level errors. Our @validate_request decorator returns
      // { message: "Validation error", errors: [...] }; FastAPI-style uses `detail`.
      // Surface these BEFORE the generic `message` so the bad field is named.
      const fieldErrors = Array.isArray(data?.errors)
        ? data.errors
        : (Array.isArray(data?.detail) ? data.detail : null);
      if (fieldErrors && fieldErrors.length) {
        const messages = fieldErrors.map((err: any) => {
          const loc = Array.isArray(err.loc)
            ? err.loc.filter((p: any) => p !== 'body').join('.')
            : (err.loc || 'field');
          const msg = err.msg || err.message || 'Invalid value';
          return `${loc}: ${msg}`;
        });
        return Promise.reject(new Error(messages.join('; ')));
      }
      if (data?.detail && typeof data.detail === 'string') {
        return Promise.reject(new Error(data.detail));
      }
      if (data?.message) {
        return Promise.reject(new Error(data.message));
      }
    }

    if (data?.exception || data?._error_message) {
      const msg = data._error_message || data.exception;
      return Promise.reject(new Error(msg));
    }
    return Promise.reject(error);
  }
);

attachDeployDetector(api);

// Auth
export async function login(email: string, password: string, deviceId?: string) {
  const payload: any = { email, password };
  if (deviceId) {
    payload.device_id = deviceId;
  }
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.login`, payload);
  return data.message || data;
}

export async function refreshToken(token: string) {
  const { data } = await api.post(`${M}/ski_delivery.api._auth_resort_admin.refresh_token`, { refresh_token: token });
  return data.message || data;
}

export async function logout(token?: string, allDevices = false) {
  const payload: any = { all_devices: allDevices };
  if (token) {
    payload.refresh_token = token;
  }
  const { data } = await api.post(`${M}/ski_delivery.api._auth_resort_admin.logout`, payload);
  return data.message || data;
}

export async function whoami() {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.whoami`);
  return data.message || data;
}

// Resort Admin Users
export async function listResortAdminUsers(q?: string, scope_type?: string, page = 1, pageSize = 50) {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  if (scope_type) params.append('scope_type', scope_type);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_resort_admin_users?${params.toString()}`);
  return data.message || data;
}

export async function getResortAdminUser(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_resort_admin_user?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function createResortAdminUser(payload: {
  email: string;
  password?: string;
  status?: 'Active' | 'Disabled';
  role: string; // 'Partner Admin' | 'Partner Courier' | 'Support'
  scope_type: 'restaurant' | 'grocery' | 'all';
  restaurant?: string;
  grocery?: string;
  restaurants?: string[];
  groceries?: string[];
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_resort_admin_user`, payload);
  return data.message || data;
}

export async function verifyPasswordToken(token: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.verify_password_token?token=${encodeURIComponent(token)}`);
  return data.message || data;
}

export async function setPassword(token: string, newPassword: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.set_password`, { token, new_password: newPassword });
  return data.message || data;
}

export async function updateResortAdminUser(payload: {
  name: string;
  password?: string;
  status?: 'Active' | 'Disabled';
  role?: string; // 'Resort Super Admin' | 'Partner Admin' | 'Partner Courier' | 'Support'
  scope_type?: 'restaurant' | 'grocery' | 'all';
  restaurants?: string[];
  groceries?: string[];
}) {
  const { name, ...body } = payload;
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_resort_admin_user?name=${encodeURIComponent(name)}`, body);
  return data.message || data;
}

export async function deleteResortAdminUser(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.delete_resort_admin_user?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function listResortAdminRoles() {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_resort_admin_roles`);
  return data.message || data;
}

export async function resendPasswordToken(userName: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.resend_password_token`, { user_name: userName });
  return data.message || data;
}

export async function changeOwnPassword(currentPassword: string, newPassword: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.change_own_password`, {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data.message || data;
}

export async function forgotPassword(email: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.forgot_password`, { email });
  return data.message || data;
}

export async function confirmEmail(token: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.confirm_email`, { token });
  return data.message || data;
}

// Restaurants
export async function listRestaurants(q?: string, page = 1, pageSize = 50, resort?: string) {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  if (resort) params.append('resort', resort);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_restaurants?${params.toString()}`);
  return data.message || data;
}

export async function updateRestaurantsOrder(orders: { name: string; order: number }[]) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_restaurants_order`, {
    restaurant_orders: orders,
  });
  return data.message || data;
}

export async function createRestaurant(payload: {
  resort_location: string;
  restaurant_name: string;
  restaurant_description: string;
  restaurant_coordinates: string;
  restaurant_type: string | string[];
  restaurant_address: string;
  working_timetable: object;
  restaurant_code?: string;
  food_type_tags?: string[];
  delivery_zone_polygon?: { lat: number; lng: number }[];
  est_delivery_time?: number;
  click_and_collect?: number;
  delivery_enabled?: number;
  delivery_scheduling_enabled?: number;
  click_and_collect_scheduling_enabled?: number;
  delivery_schedule?: Record<string, any>;
  delivery_scheduled_config?: Record<string, any>;
  click_and_collect_schedule?: Record<string, any>;
  click_and_collect_scheduled_config?: Record<string, any>;
  pricing_tier: string;
  min_order_value?: number;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_restaurant`, payload);
  return data.message || data;
}

export async function createRestaurantWithAdmin(payload: {
  resort_location: string;
  restaurant_name: string;
  restaurant_description: string;
  restaurant_coordinates: string;
  restaurant_type: string | string[];
  restaurant_address: string;
  working_timetable: object;
  admin_email: string;
  restaurant_code?: string;
  status?: string;
  food_type_tags?: string[];
  delivery_zone_polygon?: { lat: number; lng: number }[];
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_restaurant_with_admin`, payload);
  return data.message || data;
}

export async function getRestaurant(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_restaurant?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function updateRestaurant(payload: {
  restaurant_id: string;
  resort?: string;
  restaurant_name?: string;
  restaurant_address?: string;
  restaurant_description?: string;
  restaurant_coordinates?: string;
  restaurant_type?: string | string[];
  restaurant_image?: string;
  food_type_tags?: string[];
  working_timetable?: string;
  delivery_zone_polygon?: { lat: number; lng: number }[] | null;
  delivery_zones?: Array<{ resort: string; delivery_zone_polygon: { lat: number; lng: number }[] | null }>;
  clear_delivery_zone?: boolean;
  est_delivery_time?: number;
  pricing_tier?: string;
  off_now?: number;
  auto_reset_off_now?: number;
  min_order_value?: number;
  is_featured?: number;
  click_and_collect?: number;
  delivery_enabled?: number;
  scheduled_delivery_enabled?: number;
  delivery_scheduling_enabled?: number;
  click_and_collect_scheduling_enabled?: number;
  preorder_when_closed?: number;
  delivery_schedule?: string;
  scheduled_delivery_config?: string;
  delivery_scheduled_config?: string;
  click_and_collect_schedule?: string;
  click_and_collect_scheduled_config?: string;
}) {
  const { restaurant_id, ...body } = payload;
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_restaurant?restaurant_id=${encodeURIComponent(restaurant_id)}`, body);
  return data.message || data;
}

export async function uploadFile(file: File, restaurantId?: string): Promise<{ file_url: string; file_name: string; name: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('is_private', '0'); // Public file
  formData.append('folder', 'Home/Attachments');

  const token = localStorage.getItem('auth_token');
  const url = restaurantId 
    ? `${M}/ski_delivery.api.resort_admin.upload_file?restaurant_id=${encodeURIComponent(restaurantId)}`
    : `${M}/ski_delivery.api.resort_admin.upload_file`;
  
  const { data } = await api.post(url, formData, {
    headers: {
      'X-Resort-Admin-Auth': token ? `Bearer ${token}` : '',
      // Don't set Content-Type, let browser set it with boundary
    },
  });
  return data.message || data;
}

// ---- Partner onboarding document extraction (Kbis / RIB) ----
export type OnboardingDocType = 'kbis' | 'rib';

export interface OnboardingExtractionResult {
  doc_type: OnboardingDocType;
  fields: Record<string, string | null>;
  validation: Record<string, boolean | null>;
  extraction_mode: 'text' | 'vision_pdf' | 'vision_image';
  model: string;
  raw_text?: string;
}

export async function extractOnboardingDoc(
  file: File,
  docType: OnboardingDocType
): Promise<OnboardingExtractionResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('doc_type', docType);

  const token = localStorage.getItem('auth_token');
  const { data } = await api.post(
    `${M}/ski_delivery.api.onboarding.extract_document`,
    formData,
    {
      headers: {
        'X-Resort-Admin-Auth': token ? `Bearer ${token}` : '',
        // Don't set Content-Type, let browser set it with boundary
      },
      timeout: 120000, // LLM extraction can take longer than the 30s default
    }
  );
  return data.message || data;
}

export async function listFoodTags(
  isRestaurantTag?: boolean,
  isGroceryTag?: boolean
): Promise<FoodTagRecord[]> {
  const params: Record<string, string> = {};
  if (isRestaurantTag !== undefined) {
    params.is_restaurant_tag = isRestaurantTag.toString();
  }
  if (isGroceryTag !== undefined) {
    params.is_grocery_tag = isGroceryTag.toString();
  }
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_food_tags`, { params });
  return data.message || data;
}

export async function getRestaurantTypeOptions() {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_restaurant_type_options`);
  return data.message || data;
}

// Groceries
export async function listGroceries(q?: string, page = 1, pageSize = 50, resort?: string) {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  if (resort) params.append('resort', resort);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_groceries?${params.toString()}`);
  return data.message || data;
}

export async function updateGroceriesOrder(orders: { name: string; order: number }[]) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_groceries_order`, {
    grocery_orders: orders,
  });
  return data.message || data;
}

export async function createGrocery(payload: {
  resort_location: string;
  grocery_name: string;
  grocery_description?: string;
  grocery_coordinates?: string;
  grocery_type?: string;
  grocery_address?: string;
  working_timetable?: object;
  grocery_code?: string;
  grocery_food_tags?: string[];
  is_active?: number;
  delivery_zone_polygon?: { lat: number; lng: number }[];
  est_delivery_time?: number;
  click_and_collect?: number;
  delivery_enabled?: number;
  delivery_scheduling_enabled?: number;
  click_and_collect_scheduling_enabled?: number;
  delivery_schedule?: Record<string, any>;
  delivery_scheduled_config?: Record<string, any>;
  click_and_collect_schedule?: Record<string, any>;
  click_and_collect_scheduled_config?: Record<string, any>;
  pricing_tier: string;
  min_order_value?: number;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_grocery`, payload);
  return data.message || data;
}

export async function createGroceryWithAdmin(payload: {
  resort_location: string;
  grocery_name: string;
  admin_email: string;
  grocery_description?: string;
  grocery_coordinates?: string;
  grocery_type?: string;
  grocery_address?: string;
  working_timetable?: object;
  grocery_code?: string;
  status?: string;
  grocery_food_tags?: string[];
  delivery_zone_polygon?: { lat: number; lng: number }[];
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.create_grocery_with_admin`, payload);
  return data.message || data;
}

export async function getGrocery(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_grocery?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function updateGrocery(payload: {
  grocery_id: string;
  resort_location?: string;
  grocery_name?: string;
  grocery_description?: string;
  grocery_coordinates?: string;
  grocery_type?: string;
  grocery_address?: string;
  grocery_image?: string;
  grocery_food_tags?: string[];
  working_timetable?: string;
  is_active?: number;
  notes?: string;
  delivery_zone_polygon?: { lat: number; lng: number }[] | null;
  delivery_zones?: Array<{ resort: string; delivery_zone_polygon: { lat: number; lng: number }[] | null }>;
  clear_delivery_zone?: boolean;
  est_delivery_time?: number;
  pricing_tier?: string;
  off_now?: number;
  auto_reset_off_now?: number;
  min_order_value?: number;
  is_featured?: number;
  click_and_collect?: number;
  delivery_enabled?: number;
  scheduled_delivery_enabled?: number;
  delivery_scheduling_enabled?: number;
  click_and_collect_scheduling_enabled?: number;
  preorder_when_closed?: number;
  delivery_schedule?: string;
  scheduled_delivery_config?: string;
  delivery_scheduled_config?: string;
  click_and_collect_schedule?: string;
  click_and_collect_scheduled_config?: string;
}) {
  const { grocery_id, ...body } = payload;
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_grocery?grocery_id=${encodeURIComponent(grocery_id)}`, body);
  return data.message || data;
}

export async function uploadGroceryFile(file: File, groceryId?: string): Promise<{ file_url: string; file_name: string; name: string }> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('is_private', '0'); // Public file
  formData.append('folder', 'Home/Attachments');

  const token = localStorage.getItem('auth_token');
  const url = groceryId
    ? `${M}/ski_delivery.api.resort_admin.upload_file?grocery_id=${encodeURIComponent(groceryId)}`
    : `${M}/ski_delivery.api.resort_admin.upload_file`;

  const { data } = await api.post(url, formData, {
    headers: {
      'X-Resort-Admin-Auth': token ? `Bearer ${token}` : '',
      // Don't set Content-Type, let browser set it with boundary
    },
  });
  return data.message || data;
}

export async function getGroceryTypeOptions() {
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_grocery_type_options`);
  return data.message || data;
}

// Unified Food Tags CRUD
export interface FoodTagRecord {
  name: string;
  food_tag_name: string;
  is_active: number;
  is_restaurant_tag: number;
  is_grocery_tag: number;
  available_as_search_filter: number;
  icon_svg?: string | null;
}

export async function adminListFoodTags(): Promise<FoodTagRecord[]> {
  const { data } = await api.get(`${M}/ski_delivery.api.food_tags.admin_list_food_tags`);
  return data.message || data;
}

export async function createFoodTag(payload: {
  food_tag_name: string;
  is_restaurant_tag?: number;
  is_grocery_tag?: number;
  is_active?: number;
  available_as_search_filter?: number;
  icon_svg?: string | null;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.food_tags.create_food_tag`, payload);
  return data.message || data;
}

export async function updateFoodTag(payload: {
  tag_id: string;
  food_tag_name?: string;
  is_restaurant_tag?: number;
  is_grocery_tag?: number;
  is_active?: number;
  available_as_search_filter?: number;
  icon_svg?: string | null;
  clear_icon?: boolean;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.food_tags.update_food_tag`, payload);
  return data.message || data;
}

export async function deleteFoodTag(tagId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.food_tags.delete_food_tag`, { tag_id: tagId });
  return data.message || data;
}

// Accommodations (chalet / hotel / apartment catalogue used by the checkout
// address picker in the client app)
export interface AccommodationRecord {
  name: string;
  accommodation_name: string;
  resort: string | null;
  /** Raw label from the source spreadsheet; "Meribel" where the resort is "Méribel". */
  resort_label: string | null;
  /** Readable name of the linked ResortLocation, the same spelling the filter uses. */
  resort_name?: string | null;
  address: string;
  latitude: number | null;
  longitude: number | null;
  geocode_status: 'Pending' | 'Resolved' | 'Failed' | 'Manual' | null;
  google_maps_link: string | null;
  is_active: number;
}

export async function adminListAccommodations(params: {
  query?: string;
  resort?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<{ items: AccommodationRecord[]; total: number }> {
  const { data } = await api.get(`${M}/ski_delivery.api.accommodations.admin_list_accommodations`, {
    params: {
      query: params.query || undefined,
      resort: params.resort || undefined,
      limit: params.limit ?? 200,
      offset: params.offset ?? 0,
    },
  });
  return data.message || data;
}

export async function createAccommodation(payload: {
  accommodation_name: string;
  address: string;
  resort?: string | null;
  resort_label?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  google_maps_link?: string | null;
  is_active?: number;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.accommodations.create_accommodation`, payload);
  return data.message || data;
}

export async function updateAccommodation(payload: {
  accommodation_id: string;
  accommodation_name?: string;
  address?: string;
  resort?: string | null;
  resort_label?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  google_maps_link?: string | null;
  is_active?: number;
  clear_coordinates?: boolean;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.accommodations.update_accommodation`, payload);
  return data.message || data;
}

export async function deleteAccommodation(accommodationId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.accommodations.delete_accommodation`, {
    accommodation_id: accommodationId,
  });
  return data.message || data;
}

export async function geocodeAccommodations(payload: { limit?: number; include_failed?: boolean } = {}) {
  const { data } = await api.post(`${M}/ski_delivery.api.accommodations.geocode_accommodations`, {
    limit: payload.limit ?? 100,
    include_failed: payload.include_failed ? 1 : 0,
  });
  return data.message || data;
}

// Resort Locations (for dropdowns)
export async function getResortLocations() {
  const { data } = await api.post(`${M}/ski_delivery.api.availability.get_resort_locations`);
  return data.message || data;
}

// Resorts CRUD
export async function listResorts(q?: string, page = 1, pageSize = 50) {
  const params = new URLSearchParams();
  if (q) params.append('q', q);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.resorts.list_resorts?${params.toString()}`);
  return data.message || data;
}

export async function getResort(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.resorts.get_resort?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function createResort(payload: {
  resort_name: string;
  resort_image?: string;
  default_warehouse?: string;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resorts.create_resort`, payload);
  return data.message || data;
}

export async function updateResort(payload: {
  resort_id: string;
  resort_name?: string;
  resort_image?: string;
  default_warehouse?: string;
  clear_image?: boolean;
  coming_soon?: number;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.resorts.update_resort`, payload);
  return data.message || data;
}

export async function deleteResort(resortId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resorts.delete_resort`, { resort_id: resortId });
  return data.message || data;
}

export async function uploadResortFile(file: File, resortId?: string) {
  const formData = new FormData();
  formData.append('file', file);
  if (resortId) {
    formData.append('resort_id', resortId);
  }
  const { data } = await api.post(`${M}/ski_delivery.api.resorts.upload_resort_file`, formData);
  return data.message || data;
}

// Promo Codes
export async function listPromoCodes(
  filters?: {
    status?: string;
    discount_type?: string;
    is_redeemed?: boolean;
    start_date?: string;
    end_date?: string;
  },
  search?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  
  const { data } = await api.get(`${M}/ski_delivery.api.promo_code.admin_list_promo_codes?${params.toString()}`);
  return data.message || data;
}

export async function getPromoCode(name: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.promo_code.admin_get_promo_code?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function createPromoCode(payload: {
  code: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  start_date: string | null;
  end_date: string | null;
  min_order_amount?: number;
  max_order_amount?: number;
  restaurant?: string | null;
  merchants?: { type: string; name: string }[] | null;
  resorts?: string[] | null;
  max_uses_per_user?: number;
  status?: 'Active' | 'Inactive';
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_create_promo_code`, payload);
  return data.message || data;
}

export async function bulkGeneratePromoCodes(payload: {
  count: number;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  start_date: string | null;
  end_date: string | null;
  min_order_amount?: number;
  max_order_amount?: number;
  restaurant?: string | null;
  merchants?: { type: string; name: string }[] | null;
  resorts?: string[] | null;
  max_uses_per_user?: number;
  status?: 'Active' | 'Inactive';
  code_prefix?: string;
}) {
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_bulk_generate_promo_codes`, payload);
  return data.message || data;
}

export async function updatePromoCode(payload: {
  name: string;
  discount_type?: 'fixed' | 'percentage';
  discount_value?: number;
  min_order_amount?: number;
  max_order_amount?: number;
  restaurant?: string | null;
  merchants?: { type: string; name: string }[] | null;
  resorts?: string[] | null;
  max_uses_per_user?: number;
  start_date?: string | null;
  end_date?: string | null;
  status?: 'Active' | 'Inactive';
}) {
  // Send name in body, not as query parameter (API expects UpdatePromoCodeModel with name in body)
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_update_promo_code`, payload);
  return data.message || data;
}

export async function activatePromoCode(name: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_activate_promo_code`, { name });
  return data.message || data;
}

export async function deactivatePromoCode(name: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.promo_code.admin_deactivate_promo_code`, { name });
  return data.message || data;
}

export async function getPromoCodeOrders(promoCodeName: string) {
  const { data } = await api.get(`${M}/ski_delivery.api.promo_code.admin_get_promo_code_orders?promo_code_name=${encodeURIComponent(promoCodeName)}`);
  return data.message || data;
}

// Banner Campaigns
export type BannerTextTheme = 'Dark' | 'Light';
export type BannerCampaignStatus = 'Draft' | 'Published' | 'Expired';

export interface BannerItem {
  image: string;        // raw file_url (sent back on save)
  image_url?: string;   // absolute URL (returned by get/list, for display)
  title?: string;
  description?: string;
  link?: string;
  button_text?: string;
  text_theme: BannerTextTheme;
}

export interface BannerCampaign {
  name: string;
  resort: string | null;
  resort_name: string | null;
  all_resorts: boolean;
  start_date: string | null;
  end_date: string | null;
  is_published: boolean;
  priority: number;
  status: BannerCampaignStatus;
  banners: BannerItem[];
  banner_count?: number;
  created_by_admin?: string;
}

export interface BannerCampaignPayload {
  resort?: string | null;
  all_resorts: boolean;
  start_date: string;
  end_date: string;
  is_published: boolean;
  priority?: number;
  banners: BannerItem[];
}

export async function listBannerCampaigns(
  search?: string,
  statusFilter?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (statusFilter) params.append('status_filter', statusFilter);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const { data } = await api.get(`${M}/ski_delivery.api.banner_campaign.admin_list_banner_campaigns?${params.toString()}`);
  return data.message || data;
}

export async function getBannerCampaign(name: string): Promise<BannerCampaign> {
  const { data } = await api.get(`${M}/ski_delivery.api.banner_campaign.admin_get_banner_campaign?name=${encodeURIComponent(name)}`);
  return data.message || data;
}

export async function createBannerCampaign(payload: BannerCampaignPayload) {
  const { data } = await api.post(`${M}/ski_delivery.api.banner_campaign.admin_create_banner_campaign`, payload);
  return data.message || data;
}

export async function updateBannerCampaign(payload: BannerCampaignPayload & { name: string }) {
  const { data } = await api.post(`${M}/ski_delivery.api.banner_campaign.admin_update_banner_campaign`, payload);
  return data.message || data;
}

export async function deleteBannerCampaign(name: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.banner_campaign.admin_delete_banner_campaign`, { name });
  return data.message || data;
}

const PUSH = `${M}/ski_delivery.api.push_campaigns`;

export type PushCampaignStatus =
  | 'Draft'
  | 'Scheduled'
  | 'Sending'
  | 'Stopping'
  | 'Paused'
  | 'Sent'
  | 'Cancelled'
  | 'Failed';

export type PushAudienceType = 'test_list' | 'app_users' | 'partners' | 'couriers';

export type PushOrderState = 'any' | 'never_ordered' | 'ordered_paid' | 'unpaid_only' | 'dormant';

export type PushOrderCount = 'any' | '0' | '1' | '2+';

export type PushSendMode = 'now' | 'scheduled';

export type PushPausedReason = 'error_spike' | 'content_rejected' | 'quiet_hours' | 'manual';

export interface PushAudienceFilters {
  order_state: PushOrderState;
  order_state_days: number | null;
  resorts: string[];
  languages: string[];
  platforms: string[];
  not_seen_for_days: number | null;
  registered_from: string | null;
  registered_to: string | null;
  order_count: PushOrderCount;
}

export interface PushResortFilters {
  resorts: string[];
}

export type PushAudienceJson = PushAudienceFilters | PushResortFilters;

export interface PushEstimateRequest extends PushAudienceFilters {
  audience_type: PushAudienceType;
  campaign_name: string | null;
}

export interface PushResortOption {
  value: string;
  label: string;
}

export interface PushCampaign {
  name: string;
  campaign_name: string;
  status: PushCampaignStatus;
  audience_type: PushAudienceType;
  audience_json: PushAudienceJson | string | null;
  test_emails: string[];
  title_en: string | null;
  title_fr: string | null;
  body_en: string | null;
  body_fr: string | null;
  picture_link: string | null;
  content_hash: string | null;
  test_sent_content_hash: string | null;
  test_sent_at: string | null;
  test_send_result_json: string | null;
  send_mode: PushSendMode;
  scheduled_at: string | null;
  paused_reason: PushPausedReason | null;
  resume_after: string | null;
  failure_reason:
    | 'auth_failure'
    | 'launch_not_authorized'
    | 'stopped_by_admin'
    | 'materialization_error'
    | 'empty_audience_at_launch'
    | null;
  blocked_by_campaign: string | null;
  confirmed_recipients: number;
  materialized_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  last_progress_at: string | null;
  counters_finalized: number;
  recipients_total: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  devices_accepted_total: number;
  skipped_no_device: number;
  skipped_cancelled: number;
  skipped_account_deleted: number;
  skipped_not_active: number;
  failed_unregistered_token: number;
  failed_fcm_error: number;
  failed_max_attempts: number;
  failed_lease_thrash: number;
  excluded_not_active: number;
  excluded_no_email: number;
  excluded_no_device: number;
  estimate_json: string | null;
  created_by_admin: string | null;
  creation: string;
  modified: string;
}

export type PushCampaignStats = Pick<
  PushCampaign,
  | 'status'
  | 'paused_reason'
  | 'resume_after'
  | 'failure_reason'
  | 'modified'
  | 'recipients_total'
  | 'sent_count'
  | 'failed_count'
  | 'skipped_count'
  | 'devices_accepted_total'
  | 'skipped_no_device'
  | 'skipped_cancelled'
  | 'skipped_account_deleted'
  | 'skipped_not_active'
  | 'failed_unregistered_token'
  | 'failed_fcm_error'
  | 'failed_max_attempts'
  | 'failed_lease_thrash'
  | 'excluded_not_active'
  | 'excluded_no_email'
  | 'excluded_no_device'
  | 'counters_finalized'
  | 'started_at'
  | 'finished_at'
  | 'last_progress_at'
>;

export interface PushCampaignListItem {
  name: string;
  campaign_name: string;
  status: PushCampaignStatus;
  audience_type: PushAudienceType;
  recipients_total: number;
  sent_count: number;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  creation: string;
  modified: string;
}

export interface PushCampaignEstimate {
  audience_type: PushAudienceType;
  order_state: PushOrderState;
  total: number;
  reachable: number;
  not_active: number | null;
  no_email: number | null;
  no_device: number | null;
  frequency_capped: number | null;
  opted_out: number | null;
  approximate: boolean;
  computed_at: string | null;
}

export interface PushTestSendResult {
  results: {
    email: string;
    status: 'sent' | 'skipped';
    reason:
      | 'no_device'
      | 'cancelled'
      | 'account_deleted'
      | 'not_active'
      | 'unregistered_token'
      | 'fcm_error'
      | 'max_attempts'
      | 'lease_thrash'
      | null;
  }[];
  content_hash: string;
}

export interface PushCampaignPayload {
  campaign_name: string;
  audience_type: PushAudienceType;
  audience_json: PushAudienceJson;
  test_emails: string[];
  title_en: string;
  title_fr: string;
  body_en: string;
  body_fr: string;
  picture_link: string;
  send_mode: PushSendMode;
  scheduled_at: string | null;
}

export const PUSH_CAMPAIGN_ERROR_CODES = [
  'insufficient_role',
  'field_not_writable',
  'campaign_not_editable',
  'invalid_status_transition',
  'timestamp_mismatch',
  'content_invalid',
  'invalid_picture_link',
  'test_email_not_allowlisted',
  'test_send_rate_limited',
  'test_send_required',
  'not_confirmed',
  'recipient_count_drift',
  'recipient_limit_exceeded',
  'empty_audience',
  'firebase_unavailable',
  'scheduled_in_past',
  'another_campaign_running',
  'order_filter_not_applicable',
  'invalid_audience_filter',
  'estimate_timeout',
  'estimate_in_progress',
  'estimate_rate_limited',
  'quiet_hours',
  'push_campaigns_disabled',
  'unsafe_rate_config',
  'campaign_not_found',
] as const;

export type PushCampaignErrorCode = (typeof PUSH_CAMPAIGN_ERROR_CODES)[number];

export function getPushCampaignErrorCode(error: unknown): PushCampaignErrorCode | null {
  const payload = (error as { response?: { data?: { error_code?: unknown; }; }; })?.response?.data;
  const code = payload?.error_code;
  if (typeof code !== 'string') {
    return null;
  }
  const known = PUSH_CAMPAIGN_ERROR_CODES.find(x => x === code);
  return known ?? null;
}

export function getPushQuietHoursNextAllowedAt(error: unknown): string | null {
  if (getPushCampaignErrorCode(error) !== 'quiet_hours') {
    return null;
  }
  const payload = (error as { response?: { data?: { next_allowed_at?: unknown; }; }; })?.response?.data;
  const value = payload?.next_allowed_at;
  if (typeof value !== 'string' || value === '') {
    return null;
  }
  return value;
}

export function isPushCampaignConflict(error: unknown): boolean {
  const code = getPushCampaignErrorCode(error);
  return code === 'timestamp_mismatch' || code === 'campaign_not_editable';
}

function abortableConfig(signal: AbortSignal | null): AxiosRequestConfig {
  if (signal == null) {
    return {};
  }
  return { signal, skipDeployDetector: true };
}

export async function listPushCampaigns(
  page = 1,
  pageSize = 25,
  signal: AbortSignal | null = null
): Promise<{ data: PushCampaignListItem[]; pagination: { page: number; page_size: number; total: number; }; }> {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());
  const response = await api.get(`${PUSH}.admin_list_campaigns?${params.toString()}`, abortableConfig(signal));
  return response.data.message || response.data;
}

export async function getPushCampaign(name: string): Promise<PushCampaign> {
  const response = await api.get(`${PUSH}.admin_get_campaign?name=${encodeURIComponent(name)}`);
  return response.data.message || response.data;
}

export async function createPushCampaign(payload: PushCampaignPayload): Promise<PushCampaign> {
  const response = await api.post(`${PUSH}.admin_create_campaign`, payload);
  return response.data.message || response.data;
}

export async function updatePushCampaign(
  payload: PushCampaignPayload & { name: string; modified: string; }
): Promise<PushCampaign> {
  const response = await api.post(`${PUSH}.admin_update_campaign`, payload);
  return response.data.message || response.data;
}

export async function deletePushCampaign(name: string, modified: string): Promise<{ ok: boolean; }> {
  const response = await api.post(`${PUSH}.admin_delete_campaign`, { name, modified });
  return response.data.message || response.data;
}

export async function estimatePushAudience(
  payload: PushEstimateRequest,
  signal: AbortSignal | null = null
): Promise<PushCampaignEstimate> {
  const response = await api.post(`${PUSH}.admin_estimate_audience`, payload, abortableConfig(signal));
  return response.data.message || response.data;
}

export async function testSendPushCampaign(name: string, modified: string): Promise<PushTestSendResult> {
  const response = await api.post(`${PUSH}.admin_test_send`, { name, modified });
  return response.data.message || response.data;
}

export async function launchPushCampaign(payload: {
  name: string;
  modified: string;
  confirmed: boolean;
  confirmed_recipients: number;
  send_mode: PushSendMode;
  scheduled_at: string | null;
}): Promise<PushCampaign> {
  const response = await api.post(`${PUSH}.admin_launch_campaign`, payload);
  return response.data.message || response.data;
}

export async function cancelPushCampaign(name: string, modified: string): Promise<PushCampaign> {
  const response = await api.post(`${PUSH}.admin_cancel_campaign`, { name, modified });
  return response.data.message || response.data;
}

export async function stopPushCampaign(name: string, modified: string): Promise<PushCampaign> {
  const response = await api.post(`${PUSH}.admin_stop_campaign`, { name, modified });
  return response.data.message || response.data;
}

export async function pausePushCampaign(name: string, modified: string): Promise<PushCampaign> {
  const response = await api.post(`${PUSH}.admin_pause_campaign`, { name, modified });
  return response.data.message || response.data;
}

export async function resumePushCampaign(name: string, modified: string): Promise<PushCampaign> {
  const response = await api.post(`${PUSH}.admin_resume_campaign`, { name, modified });
  return response.data.message || response.data;
}

export async function downloadPushCampaignFailedCsv(name: string, includeEmails: boolean): Promise<Blob> {
  try {
    const response = await api.post(
      `${PUSH}.admin_campaign_failed_csv`,
      { name, include_emails: includeEmails ? '1' : '0' },
      { responseType: 'blob' }
    );
    return response.data;
  } catch (failure) {
    throw await parseBlobErrorBody(failure);
  }
}

async function parseBlobErrorBody(failure: unknown): Promise<unknown> {
  const response = (failure as { response?: { data?: unknown; }; })?.response;
  if (response == null || !(response.data instanceof Blob)) {
    return failure;
  }
  try {
    response.data = JSON.parse(await response.data.text());
  } catch {
    return failure;
  }
  return failure;
}

export async function listPushResortOptions(): Promise<PushResortOption[]> {
  const response = await getResortLocations();
  const locations = response?.resort_locations;
  if (!Array.isArray(locations)) {
    return [];
  }
  return locations
    .filter((x: { value?: unknown; }) => typeof x?.value === 'string' && x.value !== '')
    .map((x: { value: string; label?: string; }) => ({ value: x.value, label: x.label || x.value }));
}

export type PushRecipientStatus = 'Pending' | 'Claimed' | 'Sent' | 'Failed' | 'Skipped';

export interface PushCampaignRecipientRow {
  user_email: string;
  status: PushRecipientStatus;
  skip_reason: string | null;
  fail_reason: string | null;
  error_detail: string | null;
  attempts: number;
  sent_at: string | null;
  devices_targeted: number | null;
  devices_accepted: number | null;
}

export interface PushCampaignRecipientPage {
  data: PushCampaignRecipientRow[];
  recipients_purged_at: string | null;
  pagination: {
    page: number;
    page_size: number;
    total: number;
  };
}

export async function listPushCampaignRecipients(
  name: string,
  status: PushRecipientStatus | 'all',
  page: number,
  pageSize: number
): Promise<PushCampaignRecipientPage> {
  const response = await api.post(`${PUSH}.admin_list_campaign_recipients`, {
    name,
    status,
    page,
    page_size: pageSize,
  });
  return response.data.message || response.data;
}

export async function getPushCampaignStats(
  name: string,
  signal: AbortSignal | null = null
): Promise<PushCampaignStats> {
  const response = await api.get(
    `${PUSH}.admin_campaign_stats?name=${encodeURIComponent(name)}`,
    abortableConfig(signal)
  );
  return response.data.message || response.data;
}

// Restaurant Publish/Unpublish
export async function publishRestaurant(restaurantId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.publish_restaurant?restaurant_id=${encodeURIComponent(restaurantId)}`);
  return data.message || data;
}

export async function unpublishRestaurant(restaurantId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.unpublish_restaurant?restaurant_id=${encodeURIComponent(restaurantId)}`);
  return data.message || data;
}

// Grocery Publish/Unpublish
export async function publishGrocery(groceryId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.publish_grocery?grocery_id=${encodeURIComponent(groceryId)}`);
  return data.message || data;
}

export async function unpublishGrocery(groceryId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.unpublish_grocery?grocery_id=${encodeURIComponent(groceryId)}`);
  return data.message || data;
}

// Orders
export async function listOrders(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  },
  search?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_orders?${params.toString()}`);
  return data.message || data;
}

export async function getOrderStats(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  }
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_order_stats?${params.toString()}`);
  return data.message || data;
}

export async function exportOrders(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  }
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.export_orders?${params.toString()}`, {
    responseType: 'blob'
  });
  return data;
}

// Billing
export async function listBilling(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  },
  search?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_billing?${params.toString()}`);
  return data.message || data;
}

export async function getBillingStats(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  }
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.get_billing_stats?${params.toString()}`);
  return data.message || data;
}

// Orders (Detailed)
export async function listOrdersDetailed(
  filters?: {
    restaurant?: string;
    status?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
  },
  search?: string,
  page = 1,
  pageSize = 50
) {
  const params = new URLSearchParams();
  if (filters) {
    params.append('filters_json', JSON.stringify(filters));
  }
  if (search) params.append('search', search);
  params.append('page', page.toString());
  params.append('page_size', pageSize.toString());

  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_orders_detailed?${params.toString()}`);
  return data.message || data;
}

// Feedback
export async function listFeedback(
  filters?: Record<string, any>,
  page = 1,
  pageSize = 25,
): Promise<{ data: any[]; total: number; page: number; page_size: number }> {
  const params: Record<string, string> = {
    page: String(page),
    page_size: String(pageSize),
  };
  if (filters && Object.keys(filters).length > 0) {
    params.filters_json = JSON.stringify(filters);
  }
  const { data } = await api.get(`${M}/ski_delivery.api.resort_admin.list_feedback`, { params });
  return data.message;
}

// Cancel Order (Resort Super Admin only)
export async function cancelOrder(orderId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.cancel_order`, { order_id: orderId });
  return data.message || data;
}

// Refund Order (Resort Super Admin only)
export async function refundOrder(orderId: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.refund_order`, { order_id: orderId });
  return data.message || data;
}

// Update Order Status (Resort Super Admin, Partner Admin, Support)
export async function updateOrderStatus(orderId: string, newStatus: string, supportNote: string) {
  const { data } = await api.post(`${M}/ski_delivery.api.resort_admin.update_order_status`, {
    order_id: orderId,
    new_status: newStatus,
    support_note: supportNote,
  });
  return data.message || data;
}

// ===========================================================================
// Restaurant menu editor (resort_admin_menu.*)
// Stores data identically to the kitchen-app (same doctypes / RestaurantService).
// ===========================================================================

const MENU = `${M}/ski_delivery.api.resort_admin_menu`;

export interface MenuSection {
  name: string;
  section_name: string;
  restaurant?: string;
  order?: number;
  creation?: string;
}

export interface ProductAddonOption {
  name: string;
  // Surcharge added to the product's base price (0 = free, no surcharge).
  price: number;
}

export interface ProductAddon {
  // Stable child-row id assigned by the backend.
  id: string;
  title: string;
  selection_type: 'single' | 'multi';
  required: boolean;
  options: ProductAddonOption[];
}

// Input shape for create/update: send `id` for existing groups so the backend
// preserves row ids; omit it for new groups. On update, always send the FULL
// addons array — an empty array clears all add-ons.
export type ProductAddonInput = Omit<ProductAddon, 'id'> & { id?: string };

export interface FoodProduct {
  name: string;
  food_name: string;
  food_description?: string;
  ingredients?: string;
  allergens?: string;
  price: number;
  status: 'Active' | 'Hidden';
  menu_section: string;
  restaurant?: string;
  item?: string;
  order?: number;
  estimated_completion_time?: number;
  food_tags?: string[];
  image?: string | null;
  addons?: ProductAddon[];
}

export interface FoodProductInput {
  menu_section: string;
  food_name: string;
  food_description?: string;
  ingredients?: string;
  allergens?: string;
  price: number | string;
  status?: 'Active' | 'Hidden';
  food_tags?: string[];
  estimated_completion_time?: number;
  addons?: ProductAddonInput[];
}

// ---- Menu sections ----
export async function listMenuSections(restaurantId: string): Promise<MenuSection[]> {
  const { data } = await api.get(`${MENU}.list_menu_sections?restaurant_id=${encodeURIComponent(restaurantId)}`);
  return data.message || data;
}

export async function createMenuSection(restaurantId: string, sectionName: string): Promise<MenuSection> {
  const { data } = await api.post(
    `${MENU}.create_menu_section?restaurant_id=${encodeURIComponent(restaurantId)}`,
    { section_name: sectionName }
  );
  return data.message || data;
}

export async function updateMenuSection(restaurantId: string, sectionName: string, newSectionName: string): Promise<MenuSection> {
  const { data } = await api.post(
    `${MENU}.update_menu_section?restaurant_id=${encodeURIComponent(restaurantId)}`,
    { section_name: sectionName, new_section_name: newSectionName }
  );
  return data.message || data;
}

export async function deleteMenuSection(restaurantId: string, sectionName: string) {
  const { data } = await api.post(
    `${MENU}.delete_menu_section?restaurant_id=${encodeURIComponent(restaurantId)}`,
    { section_name: sectionName }
  );
  return data.message || data;
}

export async function reorderMenuSections(restaurantId: string, sectionOrders: { name: string; order: number }[]) {
  const { data } = await api.post(
    `${MENU}.update_menu_sections_order?restaurant_id=${encodeURIComponent(restaurantId)}`,
    { section_orders: sectionOrders }
  );
  return data.message || data;
}

// ---- Food products ----
export async function listFoodProducts(restaurantId: string, menuSection?: string, showInactive = true): Promise<FoodProduct[]> {
  const params = new URLSearchParams({ restaurant_id: restaurantId, show_inactive: String(showInactive) });
  if (menuSection) params.set('menu_section', menuSection);
  const { data } = await api.get(`${MENU}.list_food_products?${params.toString()}`);
  return data.message || data;
}

export async function createFoodProduct(restaurantId: string, payload: FoodProductInput): Promise<FoodProduct> {
  const { data } = await api.post(
    `${MENU}.create_food_product?restaurant_id=${encodeURIComponent(restaurantId)}`,
    payload
  );
  return data.message || data;
}

export async function updateFoodProduct(
  restaurantId: string,
  payload: Partial<FoodProductInput> & { product_name: string }
): Promise<FoodProduct> {
  const { data } = await api.post(
    `${MENU}.update_food_product?restaurant_id=${encodeURIComponent(restaurantId)}`,
    payload
  );
  return data.message || data;
}

export async function deleteFoodProduct(restaurantId: string, productName: string) {
  const { data } = await api.post(
    `${MENU}.delete_food_product?restaurant_id=${encodeURIComponent(restaurantId)}`,
    { product_name: productName }
  );
  return data.message || data;
}

export async function reorderFoodProducts(restaurantId: string, productOrders: { name: string; order: number }[]) {
  const { data } = await api.post(
    `${MENU}.update_food_products_order?restaurant_id=${encodeURIComponent(restaurantId)}`,
    { product_orders: productOrders }
  );
  return data.message || data;
}

export async function uploadFoodProductImage(restaurantId: string, productName: string, fileUrl: string) {
  const { data } = await api.post(
    `${MENU}.upload_food_product_image?restaurant_id=${encodeURIComponent(restaurantId)}`,
    { product_name: productName, file_url: fileUrl }
  );
  return data.message || data;
}

// ---- AI menu import (Claude) ----
export interface ExtractedMenuProduct {
  name: string | null;
  description: string | null;
  ingredients: string | null;
  price: string | null;
}

export interface ExtractedMenuSection {
  section_name: string | null;
  products: ExtractedMenuProduct[];
}

export interface ExtractMenuResult {
  sections: ExtractedMenuSection[];
  model: string;
}

export async function extractMenu(restaurantId: string, files: File[]): Promise<ExtractMenuResult> {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));

  const token = localStorage.getItem('auth_token');
  const { data } = await api.post(
    `${MENU}.extract_menu?restaurant_id=${encodeURIComponent(restaurantId)}`,
    formData,
    {
      headers: { 'X-Resort-Admin-Auth': token ? `Bearer ${token}` : '' },
      timeout: 180000, // multi-image vision extraction can take a while
    }
  );
  return data.message || data;
}

export interface SaveMenuProduct {
  food_name: string;
  food_description?: string;
  ingredients?: string;
  allergens?: string;
  price: string | number;
  estimated_completion_time?: number;
}

export interface SaveMenuSection {
  section_name?: string;
  existing_section?: string;
  products: SaveMenuProduct[];
}

export interface SaveExtractedMenuResult {
  success: boolean;
  created_sections: number;
  created_products: number;
  errors: string[];
}

export async function saveExtractedMenu(restaurantId: string, sections: SaveMenuSection[]): Promise<SaveExtractedMenuResult> {
  const { data } = await api.post(
    `${MENU}.save_extracted_menu?restaurant_id=${encodeURIComponent(restaurantId)}`,
    { sections }
  );
  return data.message || data;
}

// ===========================================================================
// Generic menu API — one adapter that drives the shared <MenuEditor/> for both
// restaurants (api.resort_admin_menu) and groceries (api.resort_admin_grocery_menu).
// The two backend modules expose the same endpoint surface; the only differences
// (id query param, tag field name, AI-import availability, entity getter) are
// folded in here so the UI stays entity-agnostic.
// ===========================================================================

export type MenuEntityKind = 'restaurant' | 'grocery';

export interface CsvImportResult {
  success: boolean;
  created: number;
  updated: number;
  total_processed: number;
  errors: string[];
}

export interface MenuApi {
  kind: MenuEntityKind;
  backPath: string;
  supportsAiImport: boolean;
  getEntityName(id: string): Promise<string>;
  listSections(id: string): Promise<MenuSection[]>;
  createSection(id: string, name: string): Promise<MenuSection>;
  updateSection(id: string, sectionName: string, newName: string): Promise<MenuSection>;
  deleteSection(id: string, sectionName: string): Promise<any>;
  reorderSections(id: string, orders: { name: string; order: number }[]): Promise<any>;
  listProducts(id: string, showInactive?: boolean): Promise<FoodProduct[]>;
  createProduct(id: string, payload: FoodProductInput): Promise<FoodProduct>;
  updateProduct(id: string, payload: Partial<FoodProductInput> & { product_name: string }): Promise<FoodProduct>;
  deleteProduct(id: string, productName: string): Promise<{ success?: boolean; has_orders?: boolean; message?: string }>;
  reorderProducts(id: string, orders: { name: string; order: number }[]): Promise<any>;
  uploadFile(file: File, id: string): Promise<{ file_url: string; file_name: string; name: string }>;
  uploadImage(id: string, productName: string, fileUrl: string): Promise<any>;
  listTags(): Promise<string[]>;
  exportProducts(id: string): Promise<{ csv_data: string; filename: string }>;
  importProducts(id: string, file: File): Promise<CsvImportResult>;
  extractMenu?(id: string, files: File[]): Promise<ExtractMenuResult>;
  saveExtractedMenu?(id: string, sections: SaveMenuSection[]): Promise<SaveExtractedMenuResult>;
}

function makeMenuApi(kind: MenuEntityKind): MenuApi {
  const isR = kind === 'restaurant';
  const BASE = isR
    ? `${M}/ski_delivery.api.resort_admin_menu`
    : `${M}/ski_delivery.api.resort_admin_grocery_menu`;
  const idKey = isR ? 'restaurant_id' : 'grocery_id';
  const q = (id: string) => `${idKey}=${encodeURIComponent(id)}`;

  const get = async (path: string) => {
    const { data } = await api.get(`${BASE}.${path}`);
    return data.message ?? data;
  };
  const post = async (fn: string, id: string, body?: any) => {
    const { data } = await api.post(`${BASE}.${fn}?${q(id)}`, body ?? {});
    return data.message ?? data;
  };

  const menuApi: MenuApi = {
    kind,
    backPath: isR ? '/restaurants' : '/groceries',
    supportsAiImport: isR,
    async getEntityName(id) {
      const e = await (isR ? getRestaurant(id) : getGrocery(id)).catch(() => null);
      return (isR ? e?.restaurant_name : e?.grocery_name) || id;
    },
    listSections: (id) => get(`list_menu_sections?${q(id)}`),
    createSection: (id, name) => post('create_menu_section', id, { section_name: name }),
    updateSection: (id, s, n) => post('update_menu_section', id, { section_name: s, new_section_name: n }),
    deleteSection: (id, s) => post('delete_menu_section', id, { section_name: s }),
    reorderSections: (id, orders) => post('update_menu_sections_order', id, { section_orders: orders }),
    async listProducts(id, showInactive = true) {
      const list = await get(`list_food_products?${q(id)}&show_inactive=${showInactive}`);
      const arr: FoodProduct[] = Array.isArray(list) ? list : [];
      // Groceries return tags under `grocery_food_tags`; normalize to `food_tags`
      // so the editor never has to care which entity it's editing.
      return isR ? arr : arr.map((p: any) => ({ ...p, food_tags: p.grocery_food_tags ?? p.food_tags ?? [] }));
    },
    createProduct: (id, payload) => post('create_food_product', id, payload),
    updateProduct: (id, payload) => post('update_food_product', id, payload),
    deleteProduct: (id, productName) => post('delete_food_product', id, { product_name: productName }),
    reorderProducts: (id, orders) => post('update_food_products_order', id, { product_orders: orders }),
    uploadFile: (file, id) => (isR ? uploadFile(file, id) : uploadGroceryFile(file, id)),
    uploadImage: (id, productName, fileUrl) =>
      post('upload_food_product_image', id, { product_name: productName, file_url: fileUrl }),
    async listTags() {
      const tags = await listFoodTags(undefined, isR ? undefined : true).catch(() => []);
      if (!Array.isArray(tags)) return [];
      return tags.map((tag) => tag.food_tag_name).filter(Boolean);
    },
    exportProducts: (id) => get(`export_food_products?${q(id)}`),
    async importProducts(id, file) {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('auth_token');
      const { data } = await api.post(`${BASE}.import_food_products?${q(id)}`, formData, {
        headers: { 'X-Resort-Admin-Auth': token ? `Bearer ${token}` : '' },
        timeout: 300000, // large imports can take a while
      });
      return data.message ?? data;
    },
  };

  // AI photo/PDF import only exists on the restaurant admin module.
  if (isR) {
    menuApi.extractMenu = (id, files) => extractMenu(id, files);
    menuApi.saveExtractedMenu = (id, sections) => saveExtractedMenu(id, sections);
  }

  return menuApi;
}

export const restaurantMenuApi = makeMenuApi('restaurant');
export const groceryMenuApi = makeMenuApi('grocery');
